import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Result, ValidationErrors, Validator, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { CashdrawerFacade } from 'pos/facades';
import { PosScreenClassComponent, DialogIcon, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { Res, Permission } from 'core/api';
import { Command } from 'pos/common';
import { ExternalPaymentsService } from 'pos/services';
import { Cols, Col } from 'core/ui';
import { PaymentDTOFactory } from '../helpers/payments-processing';
import { Message, Modal } from 'core/ui-next/modal';

const formatMoney = UI.Format.asMoney;

type Props = { pendingTipToExtract: number } & Modal.Actions<number>;

type State = { tipToExtract: Nullable<number> };

class PendingTipScreen extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();
	private txtTipToExtract = React.createRef<UI.PriceBox<State>>();

	onComponentDidMount(): void {
		this.txtTipToExtract.current!.activate();
	}

	onComponentWillUnmount(): void {
	}

	createInitialState(_props: Props): Nullable<State> {
		return { tipToExtract: null };
	}

	protected getSize(): ScreenSize {
		return 'small';
	}


	protected getScreenId(): string {
		return 'EXTRACT_PENDING_TIP';
	}

	protected getTitle(): string {
		return Res.Web.EXTRACT_TIP_TEXT();
	}

	protected getImage(): string | SvgIcon {
		return Images.ExtractPendingTip;
	}

	protected useKeyboard(): boolean {
		return false;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOk });
	}

	private onOk = async () => {
		const valid = await this.form.current!.updateModel();
		if (valid) {
			this.close(this.state.tipToExtract!);
		}
	};

	protected getContent(): React.ReactNode {
		const { Form, PriceBox } = UI.controlsFor<State>();

		const validate = (state: State) => {
			const errors: ValidationErrors = {};

			if (state.tipToExtract! > Math.abs(this.props.pendingTipToExtract))
				ValidationErrors.add(errors, 'tipToExtract', this.props.pendingTipToExtract < 0 ? Res.Msg.MSG_TIP_AMOUNT_TO_ADD_CANNOT_BE_GREATER_THAN_PENDING_TIP_TO_ADD() : Res.Msg.MSG_TIP_AMOUNT_TO_EXTRACT_CANNOT_BE_GREATER_THAN_PENDING_TIP_TO_EXTRACT());

			return errors;
		};

		return <Form small ref={this.form} model={this.state} validator={Validator.create(validate)}>
			<DialogIcon src={Images.ExtractPendingTip} />
			<Cols>
				<Col width='*' className='text-center m-t-sm text-18'>
					{this.props.pendingTipToExtract < 0 ? Res.Web.EXTRACT_TIP_PENDING_TIP_PENDING_TIP_TO_ADD_LBL() : Res.Web.EXTRACT_TIP_PENDING_TIP_PENDING_TIP_TO_EXTRACT_LBL()}
					<br />
					<strong> {formatMoney(Math.abs(this.props.pendingTipToExtract))}</strong>
				</Col>
			</Cols>
			<Cols>
				<Col width='*' className='m-t-sm'>
					<PriceBox ref={this.txtTipToExtract} placeholder={this.props.pendingTipToExtract < 0 ? Res.Web.EXTRACT_TIP_PENDING_TIP_TO_ADD_LBL() : Res.Web.EXTRACT_TIP_PENDING_TIP_TO_EXTRACT_LBL()} linkTo={x => x.tipToExtract} allowNegative={false} required />
				</Col>
			</Cols>
		</Form >;
	}
}

declare global {
	interface CommandType {
		extractPendingTip: () => Promise<Result>
	}
}

Command.register('extractPendingTip', Preconditions.and(Preconditions.ensure(Permission.Pos_ExtractPendingTip), Preconditions.ensureIsMaster), async (): Promise<Result> => {

	if (SessionProvider.isSlave()) {
		await Message.show(Res.Msg.MSG_OPERATION_ONLY_VALID_IN_MASTER_POS());
		return Result.error();
	}

	const pendingTipToExtract = await CashdrawerFacade.getPendingTipToExtract();
	const tipToExtractResult = await Modal.show(PendingTipScreen, { pendingTipToExtract: pendingTipToExtract });

	if (!tipToExtractResult.ok)
		return Result.error();

	const newPendingTipToExtract = await CashdrawerFacade.getPendingTipToExtract();
	if (pendingTipToExtract !== newPendingTipToExtract) {
		await Message.show(Res.Msg.MSG_PENDING_TIPS_HAVE_BEEN_CHANGED());
		return Result.error();
	}

	const service = pendingTipToExtract >= 0 ? ExternalPaymentsService.forRefund('NoDocument', null) : ExternalPaymentsService.forPay('NoDocument', null);
	const registerResult = await service.register(PaymentDTOFactory.createCash(tipToExtractResult.value));

	if (!registerResult.ok)
		return Result.error();

	const cashResult = await service.commitCashPayments(0);
	if (!cashResult.ok)
		return Result.error();


	if (pendingTipToExtract >= 0)
		await CashdrawerFacade.extractPendingTip(tipToExtractResult.value);
	else
		await CashdrawerFacade.addPendingTip(tipToExtractResult.value);

	return Result.ok();
});

